import React from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import CallbackPaymentBlock from '@/lib/components/CallbackPaymentBlock';
import PaymentHeader from '@/lib/components/PaymentHeader';
import WaitingBlock from '@/lib/components/WaitingBlock';
import { useIsSandboxMode } from '@/lib/hooks/useIsSandboxMode';
import useLocale from '@/payment/useLocale';
import {
    parseProcessingPageLocation,
    startPaymentStatusPolling,
} from '@/lib/utils/processingPage';

import styles from '../PaymentPage/PaymentPage.module.scss';

const ProcessingPage = () => {
    const { t } = useTranslation();
    const isSandboxMode = useIsSandboxMode();
    const targetWeezConfig = isSandboxMode ? 'lyriaSandboxUrl' : 'lyriaUrl';
    const lyriaUrl = window.weezConfig?.[targetWeezConfig] ?? 'http://localhost:8003';

    const { paymentRequestId } = React.useMemo(() => parseProcessingPageLocation(), []);
    const { locale, setLocaleOption } = useLocale();

    const [paymentRequestToken, setPaymentRequestToken] = React.useState();
    const [displayCallbackBlock, setDisplayCallbackBlock] = React.useState(false);
    const [isPaymentSuccess, setIsPaymentSuccess] = React.useState();
    const [redirectURL, setRedirectURL] = React.useState();
    const [donationEnabled, setDonationEnabled] = React.useState(false);
    const [shouldRedirect, setShouldRedirect] = React.useState(false);
    const [errorCode, setErrorCode] = React.useState(null);
    const [isCheckoutPlan, setIsCheckoutPlan] = React.useState(false);

    const handlePaymentResult = React.useCallback((res) => {
        setIsPaymentSuccess(res.success);
        setDisplayCallbackBlock(true);
        setDonationEnabled(res.donationEnabled ?? false);
        setErrorCode(res.data?.code);
        const newRedirectUrl = res.success
            ? paymentRequestToken?.success_callback_url
            : paymentRequestToken?.failure_callback_url;
        if (!newRedirectUrl) {
            return;
        }
        setRedirectURL(newRedirectUrl);
        if (!res.donationEnabled) {
            setShouldRedirect(true);
        }
    }, [paymentRequestToken?.success_callback_url, paymentRequestToken?.failure_callback_url]);

    React.useEffect(() => {
        if (!paymentRequestId) {
            return;
        }
        axios({
            method: 'GET',
            url: `${lyriaUrl}/payments/paymentrequests/${paymentRequestId}`,
        }).then((paymentRequestData) => {
            setPaymentRequestToken(paymentRequestData.data.payment_info);
            setIsCheckoutPlan(
                Object.keys(paymentRequestData.data.payment_info?.instalment_plan || {}).length > 0,
            );
            const urlParams = new URLSearchParams(window.location.search);
            setLocaleOption(paymentRequestData.data.payment_info.locale || urlParams.get('lng'));
        });
    }, [lyriaUrl, paymentRequestId, setLocaleOption]);

    React.useEffect(() => {
        if (!paymentRequestId || !paymentRequestToken) {
            if (!paymentRequestId) {
                setErrorCode('internal');
                setDisplayCallbackBlock(true);
            }
            return undefined;
        }

        const stopPolling = startPaymentStatusPolling({
            lyriaUrl,
            paymentRequestId,
            axios,
            onUpdate: (data) => {
                if (data.code === 'payment_being_processed') {
                    return;
                }
                if (typeof data.success === 'boolean') {
                    handlePaymentResult({
                        success: data.success,
                        donationEnabled: data.donations_enabled,
                        data: { code: data.code },
                    });
                } else {
                    setErrorCode(data.code || 'internal');
                    setDisplayCallbackBlock(true);
                }
            },
            onError: () => {
                setErrorCode('internal');
                setDisplayCallbackBlock(true);
            },
        });

        return stopPolling;
    }, [lyriaUrl, paymentRequestId, paymentRequestToken, handlePaymentResult]);

    return (
        <div className={styles.body}>
            <PaymentHeader />
            <div className={styles.content}>
                <div className={styles.content_left}>
                    <div className={styles.payment}>
                        {displayCallbackBlock ? (
                            <CallbackPaymentBlock
                                isPaymentSuccess={isPaymentSuccess}
                                redirectURL={redirectURL}
                                isTimeUp={false}
                                donationEnabled={donationEnabled}
                                shouldRedirect={shouldRedirect}
                                setShouldRedirect={setShouldRedirect}
                                lyriaUrl={lyriaUrl}
                                paymentRequestId={paymentRequestId}
                                locale={locale}
                                isCheckoutPlan={isCheckoutPlan}
                                paymentRequestToken={paymentRequestToken}
                                errorCode={errorCode}
                            />
                        ) : (
                            <WaitingBlock>
                                {t('payment_being_processed')}
                            </WaitingBlock>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProcessingPage;
